import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Sports,
  Person,
  Warning,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';
import { ReportDraft } from '../../types/matchReport.ts';

interface ReviewStepProps {
  draft: ReportDraft;
  homeTeamName: string;
  awayTeamName: string;
  isHomeTeam: boolean;
  gameDate: string;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  draft,
  homeTeamName,
  awayTeamName,
  isHomeTeam,
  gameDate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const playingPlayers = draft.appearances.filter(app => app.played);
  const spares = playingPlayers.filter(app => app.isSpare);
  const rosterPlayers = playingPlayers.filter(app => !app.isSpare);
  
  const goalCount = draft.events.filter(e => e.eventType === 'Goal').length;
  const cardCount = draft.events.filter(e => ['YellowCard', 'RedCard', 'SecondYellowRed'].includes(e.eventType)).length;
  const injuryCount = draft.events.filter(e => e.eventType === 'Injury').length;
  const cleanSheetCount = draft.events.filter(e => e.eventType === 'CleanSheet').length;

  // Only check goals for your team, not the opponent's
  const myTeamGoals = isHomeTeam ? (draft.homeGoals || 0) : (draft.awayGoals || 0);
  const goalsMismatch = myTeamGoals !== goalCount && !draft.isForfeit;

  const formatGameDate = (date: string) => {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return date;
    }
  };

  return (
    <Box sx={{ maxWidth: { xs: '100%', sm: 900 }, mx: 'auto', p: { xs: 0, sm: 2 } }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
        Review Match Report
      </Typography>
      
      <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
        Please review all details before submitting your match report.
      </Typography>

      {/* Validation Warnings */}
      {goalsMismatch && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            <Typography variant="body2">
              <strong>Goal count mismatch:</strong> Your score shows {myTeamGoals} goals for your team, 
              but you've only recorded {goalCount} goal events.
            </Typography>
          </Box>
        </Alert>
      )}

      {playingPlayers.length === 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>No players selected:</strong> You must select at least one player who participated.
        </Alert>
      )}

      {/* Game Information with Score and Event Summary */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Schedule color="primary" />
            Match Details
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3, mb: 4 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Game Date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatGameDate(gameDate)}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Total Players
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {playingPlayers.length} ({rosterPlayers.length} roster + {spares.length} spares)
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Events Summary
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`${goalCount} Goals`} size="small" color="primary" clickable={false} onClick={() => {}} />
                <Chip label={`${cardCount} Cards`} size="small" color="warning" clickable={false} onClick={() => {}} />
                {cleanSheetCount > 0 && (
                  <Chip label={`${cleanSheetCount} Clean Sheets`} size="small" color="success" clickable={false} onClick={() => {}} />
                )}
                {injuryCount > 0 && (
                  <Chip label={`${injuryCount} Injuries`} size="small" color="error" clickable={false} onClick={() => {}} />
                )}
              </Box>
            </Box>
          </Box>

          {/* Final Score Section */}
          <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Sports color="primary" />
              Final Score
            </Typography>
            
            {draft.isForfeit ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" color="error.main" sx={{ mb: 1 }}>
                  FORFEIT
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Game was forfeited
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 2, sm: 4 }, mb: 2 }}>
                  <Typography color="primary.main" sx={{ fontWeight: 'bold', fontSize: { xs: '2.5rem', sm: '3.75rem' } }}>
                    {draft.homeGoals ?? 0}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '2rem', sm: '3rem' } }}>
                    -
                  </Typography>
                  <Typography color="primary.main" sx={{ fontWeight: 'bold', fontSize: { xs: '2.5rem', sm: '3.75rem' } }}>
                    {draft.awayGoals ?? 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 4 } }}>
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' }, color: 'text.secondary', wordBreak: 'break-word', textAlign: 'center' }}>
                    {homeTeamName}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' }, color: 'text.secondary', wordBreak: 'break-word', textAlign: 'center' }}>
                    {awayTeamName}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Players with Events */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Person color="primary" />
            Players ({playingPlayers.length})
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Roster Players: {rosterPlayers.length} • Spares: {spares.length}
          </Typography>

          {isMobile ? (
            /* Mobile: player cards */
            <Stack spacing={1} data-testid="mobile-players-list">
              {playingPlayers
                .sort((a, b) => {
                  if (a.isSpare !== b.isSpare) return a.isSpare ? 1 : -1;
                  return (a.playerName || '').localeCompare(b.playerName || '');
                })
                .map((player) => {
                  const playerEvents = draft.events.filter(e => e.userTenantId === player.userTenantId);
                  const goals = playerEvents.filter(e => e.eventType === 'Goal').length;
                  const yellowCards = playerEvents.filter(e => e.eventType === 'YellowCard').length;
                  const redCards = playerEvents.filter(e => e.eventType === 'RedCard' || e.eventType === 'SecondYellowRed').length;
                  const injuries = playerEvents.filter(e => e.eventType === 'Injury').length;

                  return (
                    <Paper key={player.userTenantId} variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {player.isSpare ? (
                          <Chip label="S" size="small" color="warning" variant="outlined" clickable={false} onClick={() => {}} />
                        ) : (
                          <CheckCircle color="success" fontSize="small" />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, wordBreak: 'break-word' }}>
                          {player.playerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {player.shirtNumber ? `#${player.shirtNumber}` : 'No number assigned'}
                        </Typography>
                      </Box>
                      {(goals > 0 || yellowCards > 0 || redCards > 0 || injuries > 0) && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pl: 3.5 }}>
                          {goals > 0 && (
                            <Typography variant="caption">{'⚽'.repeat(Math.min(goals, 5))}{goals > 5 ? ` ×${goals}` : ''}</Typography>
                          )}
                          {yellowCards > 0 && (
                            <Typography variant="caption">{'🟨'.repeat(Math.min(yellowCards, 3))}{yellowCards > 3 ? ` ×${yellowCards}` : ''}</Typography>
                          )}
                          {redCards > 0 && (
                            <Typography variant="caption">{'🟥'.repeat(Math.min(redCards, 3))}{redCards > 3 ? ` ×${redCards}` : ''}</Typography>
                          )}
                          {injuries > 0 && (
                            <Typography variant="caption">{'🚑'.repeat(Math.min(injuries, 3))}{injuries > 3 ? ` ×${injuries}` : ''}</Typography>
                          )}
                        </Box>
                      )}
                    </Paper>
                  );
                })}
            </Stack>
          ) : (
            /* Desktop: table */
            <TableContainer data-testid="desktop-players-table">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>⚽ Goals</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>🟨 Yellow</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>🟥 Red</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>🚑 Injury</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {playingPlayers
                    .sort((a, b) => {
                      if (a.isSpare !== b.isSpare) return a.isSpare ? 1 : -1;
                      return (a.playerName || '').localeCompare(b.playerName || '');
                    })
                    .map((player) => {
                      const playerEvents = draft.events.filter(e => e.userTenantId === player.userTenantId);
                      const goals = playerEvents.filter(e => e.eventType === 'Goal').length;
                      const yellowCards = playerEvents.filter(e => e.eventType === 'YellowCard').length;
                      const redCards = playerEvents.filter(e => e.eventType === 'RedCard' || e.eventType === 'SecondYellowRed').length;
                      const injuries = playerEvents.filter(e => e.eventType === 'Injury').length;

                      return (
                        <TableRow key={player.userTenantId} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {player.isSpare ? (
                                <Chip label="S" size="small" color="warning" variant="outlined" clickable={false} onClick={() => {}} />
                              ) : (
                                <CheckCircle color="success" fontSize="small" />
                              )}
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {player.playerName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {player.shirtNumber ? `#${player.shirtNumber}` : 'No number assigned'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {goals > 0 ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                {Array.from({ length: goals }, (_, i) => (
                                  <span key={i} style={{ fontSize: '1.2em' }}>⚽</span>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {yellowCards > 0 ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                {Array.from({ length: yellowCards }, (_, i) => (
                                  <span key={i} style={{ fontSize: '1.2em' }}>🟨</span>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {redCards > 0 ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                {Array.from({ length: redCards }, (_, i) => (
                                  <span key={i} style={{ fontSize: '1.2em' }}>🟥</span>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {injuries > 0 ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                {Array.from({ length: injuries }, (_, i) => (
                                  <span key={i} style={{ fontSize: '1.2em' }}>🚑</span>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Notes Section */}
      {draft.notes && (
        <Card elevation={2} sx={{ mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Match Notes
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {draft.notes}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Final Validation */}
      <Box sx={{ mt: 4 }}>
        {playingPlayers.length === 0 ? (
          <Alert severity="error" sx={{ p: 3 }}>
            <Typography variant="body1">
              <strong>Cannot submit:</strong> Please select at least one player in the appearances step.
            </Typography>
          </Alert>
        ) : goalsMismatch ? (
          <Alert severity="warning" sx={{ p: 3 }}>
            <Typography variant="body1">
              <strong>Warning:</strong> Goal count mismatch detected. You can still submit, but please verify the information is correct.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="success" sx={{ p: 3 }}>
            <Typography variant="body1">
              <strong>Ready to submit:</strong> All required information has been provided.
            </Typography>
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default ReviewStep;